import React from 'react';

import PropTypes from 'prop-types';
import {
  Switch,
  useRouteMatch,
  Route,
  Redirect,
  useLocation,
} from 'react-router-dom';
import { IoCartSharp } from 'react-icons/io5';
import { useFormikContext } from 'formik';
import cn from 'classnames';
import { useQueryParam } from 'use-query-params';

import { Button } from 'modules/common/input';
import { AUTH_PROFILE, AUTH_TYPE } from 'modules/partial/constants';
import EmptyState from 'modules/common/components/EmptyState';
import { formatCurrency } from 'modules/common/utils';
import { useGetSelectedBranch } from 'modules/merchant/branches/hooks';
import { PageLoader } from 'modules/common';

import Header from '../services/components/Header';
import Cart from '../services/components/cart/Cart';
import { ROUTES, PAYMENT_ROUTES } from './Routes';
import { useGetCart } from '../services/hooks';
import Order from './order';
import { showDrawer } from '../../../common/drawer/DrawerMarker';
import { useGenerateDraft } from './order/hooks';

function OrderTaker({ type, canProceed, serviceType }) {
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [cartTotal, setCartTotal] = React.useState(0);
  const data = React.useContext(AUTH_PROFILE);
  const authType = React.useContext(AUTH_TYPE);
  const parentMatch = useRouteMatch();
  const match = useRouteMatch(`${parentMatch.path}/:navigation_code`);
  const [cart, , included] = useGetCart();
  const location = useLocation();
  const { state } = location || {};
  const [orderUuid] = useQueryParam('order_uuid');

  const [isGenerateDraftLoading, , generateDraft] = useGenerateDraft();

  const { navigation_code } = match?.params || {};

  const hasSelectedBranch = canProceed(true, {
    withToast: false,
  })();

  const { merchant_uuid, branch_uuid, key } = parentMatch?.params || {};
  const { values } = useFormikContext() || {};
  const [, branchProfile] = useGetSelectedBranch(merchant_uuid, branch_uuid, {
    disableAction: true,
  });

  React.useEffect(() => {
    if (type !== 'CUSTOMER') return;

    generateDraft({ qrKey: key });
  }, [type, key, generateDraft]);

  const getBranch = (v) => {
    let $branch = v?.branch || {
      ...branchProfile,
      value: branchProfile?.uuid,
      label: branchProfile?.branch_name,
    };

    if (authType === 'BRANCH') {
      $branch = {
        ...data,
        label: data?.branch_name,
        value: data?.uuid,
      };
    }

    return $branch;
  };

  const handleOnShowCart = () => {
    showDrawer({
      noTitle: true,
      content: (onClose) => (
        <Cart
          onClose={onClose}
          parentMatch={parentMatch}
          type={type}
          canProceed={canProceed}
          withTable
          serviceType={serviceType}
          branch={getBranch(values)}
          orderUuid={orderUuid}
          locationState={state}
          qrKey={key}
        />
      ),
      subTitleClassName: 'hidden',
      wrapperClassName: '!p-0 !pt-3 bg-[#262225] text-white',
    });
  };

  if (isGenerateDraftLoading) {
    return <PageLoader />;
  }

  return (
    <div className="flex flex-col flex-1 fixed top-0 left-0 w-full bg-white h-full z-50">
      <div
        className={cn(
          'grid grid-cols-11 flex-1 overflow-hidden mb-[60px] md:mb-0',
          {
            '!mb-0': showDropdown || navigation_code === 'receipt',
          }
        )}
      >
        <div className="flex flex-col col-span-11 md:col-span-7 xl:col-span-8 flex-1 overflow-hidden">
          <div className="z-20">
            {type !== 'CUSTOMER' && (
              <Header
                type={type}
                showDropdown={showDropdown}
                setShowDropdown={setShowDropdown}
                branch={getBranch(values)}
                titleClassName="text-sm leading-none md:text-xl md:leading-normal mb-1 md:mb-0"
                title="DIGITAL MENU"
              />
            )}
          </div>

          <div className="flex flex-col flex-1 overflow-hidden">
            {/* {type !== 'CUSTOMER' && (
              <div className="px-3 lg:px-10 bg-gray-50">
                <TabNavigation
                  links={ROUTES}
                  rootUrl={parentMatch?.url}
                  selected={navigation_code}
                />
              </div>
            )} */}

            <div className="flex flex-col flex-1 overflow-hidden">
              <Switch>
                {hasSelectedBranch ? (
                  ROUTES?.map((item) => (
                    <Route
                      key={item?.key}
                      path={`${parentMatch?.path}${item?.to}`}
                    >
                      <item.component
                        canProceed={canProceed}
                        type={type}
                        branch={getBranch(values)}
                      />
                    </Route>
                  ))
                ) : (
                  <EmptyState
                    contentClassName="!space-y-0"
                    containerClassName="!bg-transparent"
                    {...(!hasSelectedBranch && {
                      label: 'No branch selected',
                      note: 'To proceed, select a branch first.',
                    })}
                  />
                )}

                {(cart?.length > 0 || state?.cart?.length > 0) &&
                  PAYMENT_ROUTES?.map((item) => (
                    <Route
                      key={item?.key}
                      path={`${parentMatch?.path}${item?.to}`}
                    >
                      <item.component
                        parentMatch={parentMatch}
                        canProceed={canProceed}
                        includes={included}
                        branch={getBranch(values)}
                        type={type}
                      />
                    </Route>
                  ))}

                {type !== 'CUSTOMER' ? (
                  <Redirect to={`${parentMatch?.url}${ROUTES?.[0]?.to}`} />
                ) : (
                  <Order type={type} />
                )}
              </Switch>
            </div>
          </div>
        </div>

        <div className="hidden md:flex flex-col col-span-4 xl:col-span-3 bg-[#262225] text-white p-3 lg:p-5 lg:py-4">
          <Cart
            parentMatch={parentMatch}
            type={type}
            canProceed={canProceed}
            branch={getBranch(values)}
            serviceType={serviceType}
            withTable
            orderUuid={orderUuid}
            locationState={state}
            setCartTotal={setCartTotal}
          />
        </div>
      </div>

      {!showDropdown && navigation_code !== 'receipt' && (
        <div className="fixed bottom-0 left-0 w-full md:hidden bg-white shadow p-1.5 border-t border-gray-100">
          <Button
            className="w-full btn primary"
            label={
              type !== 'CUSTOMER' ? (
                <div className="text-white flex items-center rounded w-full">
                  <div className="flex-1 flex font-bold">
                    <div className="bg-primary-600 !bg-opacity-50 p-1.5 min-w-[26px] text-center rounded-full leading-none">
                      {cart?.length}
                    </div>
                  </div>
                  <div className="flex-shrink-0 flex-1 text-center font-bold flex items-center gap-2 justify-center">
                    <IoCartSharp className="h-5 w-5" />
                    <div className="flex-shrink-0">View Cart</div>
                  </div>
                  <div className="flex-1 text-right font-bold">
                    {included?.subtotal()
                      ? formatCurrency(cartTotal || 0)
                      : formatCurrency(0)}
                  </div>
                </div>
              ) : (
                <div className="flex">
                  <div className="flex gap-2 mx-auto">
                    <div className="flex-shrink-0 flex-1 text-center font-bold flex items-center gap-2 justify-center ">
                      <IoCartSharp className="h-5 w-5" />
                      <div className="flex-shrink-0">View Cart</div>
                    </div>
                    <div className="font-bold bg-primary-600 !bg-opacity-50 p-1.5 min-w-[26px] text-center rounded-full leading-none">
                      {cart?.length}
                    </div>
                  </div>
                </div>
              )
            }
            onClick={handleOnShowCart}
          />
        </div>
      )}
    </div>
  );
}

OrderTaker.defaultProps = {
  type: 'BRANCH',
  canProceed:
    (event) =>
    (...args) =>
      typeof event === 'function' ? event(args) : event,
  serviceType: 'order-taker',
};

OrderTaker.propTypes = {
  type: PropTypes.string,
  serviceType: PropTypes.string,
  canProceed: PropTypes.instanceOf(Function),
};

export default OrderTaker;
